import React from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { platformName } from '../platformMeta';
import { ConnectOutcome } from '../hooks/useConnectReturn';
import { SocialLoginVariant, SocialPlatform } from '../types';
import { connectErrorMessage } from './connectOutcomeMessage';
import { InstagramLoginAction } from './InstagramLoginAction';
import { withBrandWord } from '../../components/primitives/BrandWord';

interface ConnectResultAlertProps {
  outcome: ConnectOutcome;
  /** The Instagram channel has `alternativeLogin === 'instagram'`: the server can start that login. */
  offersInstagramLogin?: boolean;
  disabled?: boolean;
  onDismiss: () => void;
  /** Starts the connect flow. The Instagram sign-in action calls it. */
  onConnect?: (platform: SocialPlatform, loginVariant?: SocialLoginVariant) => void;
}

/** What the platform said when it sent the browser back, in one line. */
const ConnectResultAlertComponent: React.FC<ConnectResultAlertProps> = ({
  outcome,
  offersInstagramLogin = false,
  disabled = false,
  onDismiss,
  onConnect,
}) => {
  const intl = useIntl();
  const name = platformName(outcome.platform);

  if (outcome.result === 'connected') {
    return (
      <Alert color="success" isOpen toggle={onDismiss}>
        {intl.formatMessage(
          {
            id: 'app.sml.promote.channel.connect.done',
            defaultMessage: '{platform} is connected. We can post for you now.',
          },
          { platform: name }
        )}
      </Alert>
    );
  }

  // The login through a Facebook Page found no Instagram account. The sign-in of Instagram
  // needs no Page, so the banner offers it when the server has it.
  if (
    outcome.result === 'error' &&
    outcome.reason === 'noInstagramAccount' &&
    offersInstagramLogin &&
    onConnect !== undefined
  ) {
    return (
      <Alert color="danger" isOpen toggle={onDismiss}>
        <div>
          {intl.formatMessage({
            id: 'app.sml.promote.channels.instagramLogin.noAccount',
            defaultMessage:
              'We found no Instagram account on your Facebook Pages. Link it to a Page, or sign in with Instagram.',
          })}
        </div>
        <InstagramLoginAction
          label={intl.formatMessage({
            id: 'app.sml.promote.channels.instagramLogin.bannerAction',
            defaultMessage: 'Sign in with Instagram',
          })}
          tone="inherit"
          disabled={disabled}
          onConnect={onConnect}
        />
      </Alert>
    );
  }

  if (outcome.result === 'error') {
    return (
      <Alert color="danger" isOpen toggle={onDismiss}>
        {withBrandWord(connectErrorMessage(intl, outcome.platform, outcome.reason))}
      </Alert>
    );
  }

  return null;
};

export const ConnectResultAlert = TypedMemo(ConnectResultAlertComponent);
